import { NgClass } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Tab, TabsComponent } from 'components/tabs/tabs.component';
import { TableComponent } from 'components/table/table.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { Subject, Subscription, interval, takeUntil } from 'rxjs';
import { API_INFORMATION_REQUEST, API_INFORMATION_REQUEST_TASK, ApiService } from 'services/api.service';
import { AuthorizationService } from 'services/authorization.service';
import { NotificationService } from 'services/notification.service';
import { ToastService } from 'services/toast.service';
import { ClaimDirective } from 'src/app/directive/claim.directive';
import { FilterType, TableColumn } from 'types/table-column.interface';
import { CLAIM_SCOPES, CLAIM_TYPES } from 'types/user.interface';
import { checkClaims, getPolicyStructuralUnitIds } from 'util/user.utility';

export const KEY_LAST_ACTIVE_IR_SECTION = 'lastActiveIrSection';

interface Section {
  key: string;
  hasClaims: boolean;
}

const MY_REQUEST_STATUSES = [
  'PreliminaryResearch',
  'AdditionalInfoRequested',
  'AdditionalInfoSubmitted',
  'InProcessing'
];

const ALL_STATUSES = [
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'PreliminaryResearch',
  'AdditionalInfoRequested',
  'AdditionalInfoSubmitted',
  'InProcessing',
  'Completed',
  'Cancelled',
  'Draft'
];

const ALL_TASK_STATUSES = ['Waiting', 'Assigned', 'Accepted', 'Completed', 'Cancelled'];

const DEPARTMENT_STATUSES = [
  'NewPortal',
  'NewBackoffice',
  'NewRedirected',
  'PreliminaryResearch',
  'AdditionalInfoRequested',
  'AdditionalInfoSubmitted',
  'InProcessing',
  'Completed',
  'Cancelled',
  'Draft'
];

@Component({
  selector: 'app-info-request-workspace-page',
  standalone: true,
  imports: [TranslateModule, NgClass, TableComponent, TabsComponent, ClaimDirective, ConfirmModalComponent],
  templateUrl: './info-request-workspace-page.component.html',
  styleUrl: './info-request-workspace-page.component.scss'
})
export class InfoRequestWorkspacePageComponent implements OnInit, OnDestroy {
  public readonly CLAIM_TYPES = CLAIM_TYPES;
  public readonly POLICY_TYPE_SERVICE = CLAIM_SCOPES['informationRequest'];
  public readonly POLICY_TYPE_MY_WORKSPACE = CLAIM_SCOPES['myWorkspace'];

  @ViewChild('tasksTable') tasksTable!: TableComponent;
  @ViewChild('requestsTable') requestsTable!: TableComponent;
  @ViewChild('structuralUnitRequestsTable') structuralUnitRequestsTable!: TableComponent;
  @ViewChild('allRequestsTable') allRequestsTable!: TableComponent;
  @ViewChild('allTasksTable') allTasksTable!: TableComponent;

  private destroy$ = new Subject<void>();
  private autoRefreshSub?: Subscription;
  public readonly checkClaims = checkClaims;

  userId: string = '';
  structuralUnitIds: string[] = [];
  hasGlobalInformationRequestScope: boolean = false;

  public readonly apiPath = API_INFORMATION_REQUEST;
  public readonly taskApiPath = API_INFORMATION_REQUEST_TASK;

  // Status filter options (passed to table's topFilter)
  readonly myRequestStatusOptions = [...MY_REQUEST_STATUSES];
  readonly departmentStatusOptions = [...DEPARTMENT_STATUSES];
  readonly allStatusOptions = [...ALL_STATUSES];
  readonly myTaskStatusOptions = [...ALL_TASK_STATUSES];
  readonly allTaskStatusOptions = [...ALL_TASK_STATUSES];

  // Status counts from API responses
  requestsStatusCounts: { status: string; count: number }[] = [];
  structuralUnitStatusCounts: { status: string; count: number }[] = [];
  allRequestsStatusCounts: { status: string; count: number }[] = [];
  tasksStatusCounts: { status: string; count: number }[] = [];
  allTasksStatusCounts: { status: string; count: number }[] = [];

  // Tab counts
  requestsTotalCount: number | null = null;
  tasksActiveCount: number | null = null;

  // Task action modals
  cancelTaskId: string = '';
  completeTaskId: string = '';
  isCompleteTaskConfirmOpen: boolean = false;
  isCancelTaskConfirmOpen: boolean = false;

  sections: Section[] = [
    {
      key: 'tasks',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceMyInformationRequests'])
    },
    {
      key: 'requests',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceMyInformationRequests'])
    },
    {
      key: 'structuralUnitRequests',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceStructuralUnitInformationRequests'])
    },
    {
      key: 'allRequests',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceAllInformationRequests'])
    },
    {
      key: 'allTasks',
      hasClaims: checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceAllInformationRequests'])
    }
  ];

  private buildBaseRequestColumns(): TableColumn[] {
    return [
      {
        name: 'pages.infoRequestWorkspace.tableColumns.backofficeStatus',
        key: 'backofficeStatus',
        styleClass: 'status-column',
        valueTranslate: 'pages.service.infoRequest.backofficeStatus.',
        sortEnabled: true,
        sortKey: 'BackofficeStatus',
        multiselectFilterName: 'backofficeStatuses',
        filter: FilterType.MultiSelect
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.paymentState',
        key: 'paymentState',
        valueTranslate: 'pages.service.infoRequest.paymentState.',
        sortEnabled: true,
        sortKey: 'PaymentState',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'paymentStates',
        // LNA-2060. Fixed three-value enum, so the options are listed here rather than
        // sourced from the response like topicNames -- same as serviceDeadlines below.
        // 'None' is a real marker value, not 'no filter': it selects the rows the column
        // renders as '-'. Labels come from valueTranslate above.
        filterOptions: ['None', 'AwaitingPayment', 'Paid']
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.code',
        key: 'code',
        sortEnabled: true,
        sortKey: 'Code',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.clientFullName',
        key: 'clientFullName',
        columnClass: 'extra-wide-column',
        sortEnabled: true,
        sortKey: 'ClientFullName',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.clientIdentifier',
        key: 'clientIdentifier',
        sortEnabled: true,
        sortKey: 'ClientIdentifier',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.topicName',
        key: 'topicName',
        columnClass: 'wide-column',
        sortEnabled: true,
        sortKey: 'TopicName',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'topicNames',
        dynamicFilterOptionsField: 'topicName',
        dynamicFilterSourceField: 'topics',
        customLogic: 'topicWithRestriction'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.aboutSubject',
        key: 'aboutSubject',
        columnClass: 'wide-column',
        sortEnabled: true,
        sortKey: 'AboutSubject',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.submittedOn',
        key: 'submittedOn',
        filter: FilterType.DateTime,
        type: 'date',
        sortEnabled: true,
        sortKey: 'SubmittedOn'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.serviceDeadline',
        key: 'serviceDeadline',
        columnClass: 'wrap-header',
        sortEnabled: true,
        sortKey: 'ServiceDeadline',
        customLogic: 'deadlineWithDate',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'serviceDeadlines',
        filterOptions: ['Days30', 'Days15', 'Days10', 'Other'],
        dateFilterKey: 'deadlineDate'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.deadlineDate',
        key: 'mustCompleteByDate',
        filter: FilterType.DateTime,
        type: 'date',
        sortEnabled: true,
        sortKey: 'MustCompleteByDate'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.responsibleStructuralUnitCode',
        key: 'responsibleStructuralUnitCode',
        sortEnabled: true,
        sortKey: 'ResponsibleStructuralUnitCode',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'responsibleStructuralUnitCodes',
        dynamicFilterOptionsField: 'responsibleStructuralUnitCode',
        dynamicFilterSourceField: 'responsibleStructuralUnits'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.coResponsibleStructuralUnitCodes',
        key: 'coResponsibleStructuralUnitCodes',
        sortEnabled: true,
        sortKey: 'CoResponsibleStructuralUnitCodes',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'coResponsibleStructuralUnitCodes',
        dynamicFilterOptionsField: 'coResponsibleStructuralUnitCodes',
        dynamicFilterSourceField: 'coResponsibleStructuralUnits',
        customLogic: 'arrayJoin'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.isConsolidated',
        key: 'isConsolidated',
        customLogic: 'boolean',
        sortEnabled: true,
        sortKey: 'IsConsolidated',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'isConsolidated',
        filterOptions: ['true', 'false'],
        valueTranslate: 'pages.infoRequestWorkspace.isConsolidated.'
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.submissionChannel',
        key: 'submissionChannel',
        valueTranslate: 'pages.infoRequestWorkspace.submissionChannel.',
        sortEnabled: true,
        sortKey: 'SubmissionChannel',
        multiselectFilterName: 'submissionChannels',
        filter: FilterType.MultiSelect,
        filterOptions: ['Portal', 'Email', 'Namejs', 'Archive', 'PostalMail']
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.letterNumber',
        key: 'letterNumber',
        columnClass: 'wide-column',
        sortEnabled: true,
        sortKey: 'LetterNumber',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.phone',
        key: 'phone',
        sortEnabled: true,
        sortKey: 'Phone',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.tableColumns.email',
        key: 'email',
        sortEnabled: true,
        sortKey: 'Email',
        filter: FilterType.Search
      }
    ];
  }

  private buildMyRequestColumns(): TableColumn[] {
    const cols = this.buildBaseRequestColumns();
    cols[0].filterOptions = [...MY_REQUEST_STATUSES];
    return cols;
  }

  private buildDepartmentRequestColumns(): TableColumn[] {
    const cols = this.buildBaseRequestColumns();
    cols[0].filterOptions = [...DEPARTMENT_STATUSES];
    const coRespIndex = cols.findIndex((c) => c.key === 'coResponsibleStructuralUnitCodes');
    cols.splice(coRespIndex, 0, {
      name: 'pages.infoRequestWorkspace.tableColumns.responsibleUserFullName',
      key: 'responsibleUserFullName',
      sortEnabled: true,
      sortKey: 'ResponsibleUserFullName',
      filter: FilterType.MultiSelect,
      multiselectFilterName: 'responsibleUserFullNames',
      dynamicFilterOptionsField: 'responsibleUserFullName',
      dynamicFilterSourceField: 'responsibleUsers'
    });
    return cols;
  }

  private buildAllRequestsColumns(): TableColumn[] {
    const cols = this.buildBaseRequestColumns();
    cols[0].filterOptions = [...ALL_STATUSES];
    const coRespIndex = cols.findIndex((c) => c.key === 'coResponsibleStructuralUnitCodes');
    cols.splice(coRespIndex, 0, {
      name: 'pages.infoRequestWorkspace.tableColumns.responsibleUserFullName',
      key: 'responsibleUserFullName',
      sortEnabled: true,
      sortKey: 'ResponsibleUserFullName',
      filter: FilterType.MultiSelect,
      multiselectFilterName: 'responsibleUserFullNames',
      dynamicFilterOptionsField: 'responsibleUserFullName',
      dynamicFilterSourceField: 'responsibleUsers'
    });
    return cols;
  }

  private buildTaskColumns(): TableColumn[] {
    const cols: TableColumn[] = [
      {
        name: 'pages.infoRequestWorkspace.taskColumns.status',
        key: 'status',
        styleClass: 'task-status-column',
        valueTranslate: 'pages.infoRequestWorkspace.taskStatus.',
        sortEnabled: true,
        sortKey: 'Status',
        multiselectFilterName: 'statuses',
        filter: FilterType.MultiSelect,
        filterOptions: [...ALL_TASK_STATUSES]
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.statusChangedOn',
        key: 'statusChangedOn',
        type: 'date',
        sortEnabled: true,
        sortKey: 'StatusChangedOn',
        filter: FilterType.DateTime
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.code',
        key: 'code',
        sortEnabled: true,
        sortKey: 'Code',
        filter: FilterType.Search,
        searchFilterName: 'taskCode'
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.taskTypeName',
        key: 'taskTypeName',
        columnClass: 'wide-column',
        sortEnabled: true,
        sortKey: 'TaskTypeName',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'taskTypeClassifierValueIds',
        dynamicFilterOptionsField: 'taskTypeName',
        dynamicFilterIdField: 'taskTypeClassifierValueId'
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.text',
        key: 'text',
        columnClass: 'wide-column',
        sortEnabled: true,
        sortKey: 'Text',
        filter: FilterType.Search
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.assignedByUserFullName',
        key: 'assignedByUserFullName',
        sortEnabled: true,
        sortKey: 'AssignedByUserFullName',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'assignedByUserIds',
        dynamicFilterOptionsField: 'assignedByUserFullName',
        dynamicFilterIdField: 'assignedByUserId',
        dynamicFilterSourceField: 'assigners',
        dynamicFilterSourceIdKey: 'id',
        dynamicFilterSourceLabelKey: 'fullName'
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.assignedOn',
        key: 'assignedOn',
        type: 'date',
        sortEnabled: true,
        sortKey: 'AssignedOn',
        filter: FilterType.DateTime
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.requestCode',
        key: 'requestCode',
        sortEnabled: true,
        sortKey: 'RequestCode',
        filter: FilterType.Search,
        customLogic: 'taskRequestLink'
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.assigneeUserFullName',
        key: 'assigneeUserFullName',
        sortEnabled: true,
        sortKey: 'AssigneeUserFullName',
        filter: FilterType.MultiSelect,
        multiselectFilterName: 'executorIds',
        dynamicFilterOptionsField: 'assigneeUserFullName',
        dynamicFilterIdField: 'assigneeUserId',
        dynamicFilterSourceField: 'executors',
        dynamicFilterSourceIdKey: 'id',
        dynamicFilterSourceLabelKey: 'displayName',
        customLogic: 'assigneeWithCodes'
      },
      {
        name: 'pages.infoRequestWorkspace.taskColumns.executionDate',
        key: 'executionDate',
        sortEnabled: true,
        sortKey: 'ExecutionDate',
        filter: FilterType.DateTime,
        customLogic: 'taskExecutionDate'
      }
    ];
    return cols;
  }

  private buildMyTaskColumns(): TableColumn[] {
    const cols = this.buildTaskColumns();
    cols.push({
      name: 'pages.infoRequestWorkspace.taskColumns.actions',
      key: 'actions',
      customLogic: 'taskActionsAcceptOnly'
    });
    return cols;
  }

  readonly myRequestColumns: TableColumn[] = this.buildMyRequestColumns();
  readonly departmentRequestColumns: TableColumn[] = this.buildDepartmentRequestColumns();
  readonly allRequestsColumns: TableColumn[] = this.buildAllRequestsColumns();
  readonly myTaskColumns: TableColumn[] = this.buildMyTaskColumns();
  readonly allTaskColumns: TableColumn[] = this.buildTaskColumns();

  activeSection: string = sessionStorage.getItem(KEY_LAST_ACTIVE_IR_SECTION) || 'tasks';
  isExcelDownloading: boolean = false;

  get workspaceTabs(): Tab[] {
    return this.sections
      .filter((section) => section.hasClaims)
      .map((section) => ({
        key: section.key,
        label: 'pages.infoRequestWorkspace.' + section.key,
        suffix: this.getTabSuffix(section.key)
      }));
  }

  private getTabSuffix(key: string): string | undefined {
    switch (key) {
      case 'tasks':
        return this.tasksActiveCount ? ' (' + this.tasksActiveCount + ')' : undefined;
      case 'requests':
        return this.requestsTotalCount ? ' (' + this.requestsTotalCount + ')' : undefined;
      default:
        return undefined;
    }
  }

  get requestsDefaultFilters(): { [key: string]: string | string[] } {
    return {
      scope: 'MyRequests',
      currentUserId: this.userId
    };
  }

  get structuralUnitDefaultFilters(): { [key: string]: string | string[] } {
    return {
      scope: 'MyDepartment',
      currentStructuralUnitId: this.structuralUnitIds[0] || ''
    };
  }

  get allRequestsDefaultFilters(): { [key: string]: string | string[] } {
    return {
      scope: 'AllRequests'
    };
  }

  get tasksDefaultFilters(): { [key: string]: string | string[] } {
    return {
      scope: 'AssignedToMe',
      currentUserId: this.userId,
      currentUserStructuralUnitIds: this.structuralUnitIds
    };
  }

  get allTasksDefaultFilters(): { [key: string]: string | string[] } {
    return {
      scope: 'AssignedByAnyone'
    };
  }

  // The assigner=me convenience default only makes sense for users with personal task
  // involvement; a see-all user without My-scope (e.g. LNA system administrator) must see
  // genuinely all tasks (LNA-1979).
  private readonly hasMyIrScope = checkClaims(this.POLICY_TYPE_MY_WORKSPACE, this.CLAIM_TYPES['workspaceMyInformationRequests']);

  get allTasksInitialColumnFilters(): { [key: string]: string } {
    if (!this.hasMyIrScope) {
      return {};
    }
    return {
      AssignedByUserFullName: this.userId
    };
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    public apiService: ApiService,
    private router: Router,
    private authService: AuthorizationService,
    private notificationService: NotificationService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.userId = this.authService.getUserId();
    // Scope to the InformationRequest (inquiry) role's units only — the pooled structuralUnits
    // list merges every role's units and leaks e.g. reading-room-only archives in here.
    const { structuralUnitIds, isGlobalScope } = getPolicyStructuralUnitIds(this.authService.userSecurityInfo(), this.POLICY_TYPE_SERVICE);
    this.structuralUnitIds = structuralUnitIds;
    this.hasGlobalInformationRequestScope = isGlobalScope;

    this.notificationService.startPolling();

    this.activatedRoute.queryParams.subscribe((params) => {
      const section = params['activeSection'];
      if (section && this.sections.some((s) => s.key === section)) {
        this.activeSection = section;
      }
    });

    // Land on a tab the user can actually see — e.g. a system administrator has only the
    // All-scope claims, so the 'tasks' default would show content behind a hidden tab (LNA-1979).
    if (!this.sections.some((s) => s.key === this.activeSection && s.hasClaims)) {
      this.activeSection = this.sections.find((s) => s.hasClaims)?.key ?? this.activeSection;
    }

    this.startAutoRefresh();
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshVisibleTable());
    this.loadRequestsCount();
    this.loadTasksCount();
  }

  private loadRequestsCount(): void {
    const params = `filter.scope=MyRequests&filter.currentUserId=${this.userId}&pageSize=1&page=1`;
    this.apiService.getTableData(this.apiPath, params).subscribe((data: any) => {
      this.requestsTotalCount = data?.page?.totalCount ?? 0;
    });
  }

  private loadTasksCount(): void {
    const suIds = this.structuralUnitIds.map((id) => `filter.currentUserStructuralUnitIds=${id}`).join('&');
    const params = `filter.scope=AssignedToMe&filter.currentUserId=${this.userId}&${suIds}&pageSize=1&page=1`;
    this.apiService.getTableData(this.taskApiPath, params).subscribe((data: any) => {
      const counts: { status: string; count: number }[] = data?.statusCounts || [];
      this.tasksActiveCount = counts.filter((c) => c.status === 'Accepted' || c.status === 'Assigned').reduce((sum, c) => sum + c.count, 0);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.autoRefreshSub?.unsubscribe();
  }

  private startAutoRefresh(): void {
    const FIVE_MIN = 5 * 60 * 1000;
    this.refreshVisibleTable();
    this.autoRefreshSub = interval(FIVE_MIN)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshVisibleTable();
        this.loadRequestsCount();
        this.loadTasksCount();
      });
  }

  private refreshVisibleTable(): void {
    switch (this.activeSection) {
      case 'tasks':
        this.tasksTable?.getTableData?.();
        break;
      case 'requests':
        this.requestsTable?.getTableData?.();
        break;
      case 'structuralUnitRequests':
        this.structuralUnitRequestsTable?.getTableData?.();
        break;
      case 'allRequests':
        this.allRequestsTable?.getTableData?.();
        break;
      case 'allTasks':
        this.allTasksTable?.getTableData?.();
        break;
    }
  }

  setActiveSection(section: string): void {
    this.activeSection = section;
    sessionStorage.setItem(KEY_LAST_ACTIVE_IR_SECTION, section);
    this.refreshVisibleTable();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true
    });
  }

  // Status filter handlers
  onRequestsResponseLoaded(response: any): void {
    this.requestsStatusCounts = response.statusCounts || [];
    this.requestsTotalCount = response.page?.totalCount ?? 0;
  }

  onStructuralUnitResponseLoaded(response: any): void {
    this.structuralUnitStatusCounts = response.statusCounts || [];
  }

  onAllRequestsResponseLoaded(response: any): void {
    this.allRequestsStatusCounts = response.statusCounts || [];
  }

  onTasksResponseLoaded(response: any): void {
    this.tasksStatusCounts = response.statusCounts || [];
    const counts: { status: string; count: number }[] = response.statusCounts || [];
    this.tasksActiveCount = counts.filter((c) => c.status === 'Accepted' || c.status === 'Assigned').reduce((sum, c) => sum + c.count, 0);
  }

  onAllTasksResponseLoaded(response: any): void {
    this.allTasksStatusCounts = response.statusCounts || [];
  }

  onTaskAction(event: { action: string; row: any }): void {
    switch (event.action) {
      case 'accept':
        this.acceptTask(event.row.id);
        break;
      case 'complete':
        this.completeTaskId = event.row.id;
        this.isCompleteTaskConfirmOpen = true;
        break;
      case 'cancel':
        this.cancelTaskId = event.row.id;
        this.isCancelTaskConfirmOpen = true;
        break;
      case 'viewRequest':
        this.router.navigate([`/informacijas-pieprasijums/${event.row.informationRequestId}`]);
        break;
    }
  }

  acceptTask(taskId: string): void {
    this.apiService.acceptIrTask(taskId).subscribe({
      next: () => {
        this.toastService.showSuccess('taskAccepted', '', { shouldTranslate: true });
        this.refreshVisibleTable();
        this.loadTasksCount();
      },
      error: () => {
        this.toastService.showDanger('taskActionError', '', { shouldTranslate: true });
      }
    });
  }

  confirmCompleteTask(): void {
    this.isCompleteTaskConfirmOpen = false;
    this.apiService.completeIrTask(this.completeTaskId).subscribe({
      next: () => {
        this.toastService.showSuccess('taskCompleted', '', { shouldTranslate: true });
        this.completeTaskId = '';
        this.refreshVisibleTable();
        this.loadTasksCount();
      },
      error: () => {
        this.toastService.showDanger('taskActionError', '', { shouldTranslate: true });
      }
    });
  }

  cancelCompleteTask(): void {
    this.isCompleteTaskConfirmOpen = false;
    this.completeTaskId = '';
  }

  confirmCancelTask(): void {
    this.isCancelTaskConfirmOpen = false;
    this.apiService.cancelIrTask(this.cancelTaskId, { reason: null }).subscribe({
      next: () => {
        this.toastService.showSuccess('taskCancelled', '', { shouldTranslate: true });
        this.cancelTaskId = '';
        this.refreshVisibleTable();
        this.loadTasksCount();
      },
      error: (err) => {
        this.toastService.showValidationOrRequestError(err);
      }
    });
  }

  cancelCancelTask(): void {
    this.isCancelTaskConfirmOpen = false;
    this.cancelTaskId = '';
  }

  getExcelFile(): void {
    if (this.isExcelDownloading) {
      return;
    }
    const context = this.getActiveExportContext();
    if (!context) {
      return;
    }
    this.isExcelDownloading = true;
    const currentQueryParams = context.table?.getQueryParams() ?? new URLSearchParams();
    const topFilterSelected = context.table ? [...context.table.topFilterSelected] : [];
    const initialColumnFilters = context.table?.getResolvedInitialColumnFilters() ?? {};
    const request$ = context.isTask
      ? this.apiService.getInformationRequestTaskExcel(
          currentQueryParams,
          context.defaultFilters,
          context.topFilterParamName,
          topFilterSelected,
          initialColumnFilters
        )
      : this.apiService.getInformationRequestExcel(
          currentQueryParams,
          context.defaultFilters,
          context.topFilterParamName,
          topFilterSelected,
          initialColumnFilters
        );
    request$.subscribe({
      next: (data: Blob) => {
        if (data) {
          this.saveExcelFile(data, context.fileName);
        }
        this.isExcelDownloading = false;
      },
      error: () => {
        this.toastService.showDanger('excelDownloadError', '', { shouldTranslate: true });
        this.isExcelDownloading = false;
      }
    });
  }

  private getActiveExportContext(): {
    isTask: boolean;
    table?: TableComponent;
    defaultFilters: { [key: string]: string | string[] };
    topFilterParamName: string;
    fileName: string;
  } | null {
    switch (this.activeSection) {
      case 'tasks':
        return {
          isTask: true,
          table: this.tasksTable,
          defaultFilters: this.tasksDefaultFilters,
          topFilterParamName: 'statuses',
          fileName: 'uzdevumi.xlsx'
        };
      case 'allTasks':
        return {
          isTask: true,
          table: this.allTasksTable,
          defaultFilters: this.allTasksDefaultFilters,
          topFilterParamName: 'statuses',
          fileName: 'uzdevumi.xlsx'
        };
      case 'requests':
        return {
          isTask: false,
          table: this.requestsTable,
          defaultFilters: this.requestsDefaultFilters,
          topFilterParamName: 'backofficeStatuses',
          fileName: 'pieprasijumi.xlsx'
        };
      case 'structuralUnitRequests':
        return {
          isTask: false,
          table: this.structuralUnitRequestsTable,
          defaultFilters: this.structuralUnitDefaultFilters,
          topFilterParamName: 'backofficeStatuses',
          fileName: 'pieprasijumi.xlsx'
        };
      case 'allRequests':
        return {
          isTask: false,
          table: this.allRequestsTable,
          defaultFilters: this.allRequestsDefaultFilters,
          topFilterParamName: 'backofficeStatuses',
          fileName: 'pieprasijumi.xlsx'
        };
      default:
        return null;
    }
  }

  saveExcelFile(blob: Blob, fileName: string = 'pieprasijumi.xlsx'): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  getInfoRequestViewPath = (row: any): string => {
    if (row.backofficeStatus === 'Draft') {
      return `/pakalpojumi/informacijas-pieprasijums/${row.id}`;
    }
    return `/informacijas-pieprasijums/${row.id}`;
  };

  getTaskViewPath = (row: any): string => {
    return `/informacijas-pieprasijums/${row.informationRequestId}`;
  };

  createInfoRequest(): void {
    this.router.navigate(['/informacijas-pieprasijums/pievienot']);
  }
}
